import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { POLL_WINDOW_MS, ZALO_OA_URL, isAwaitingPayment, shouldKeepPolling } from '@repo/shared-types';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  useCancelSubscriptionInvoice,
  useMockPaySubscriptionInvoice,
  useSubscription,
  useSubscriptionInvoice,
} from '../../hooks/useSubscription';
import { BusyButton } from '../../components/BusyButton';
import { CopyButton } from './components/CopyButton';
import { PaymentConfirmation } from './components/PaymentConfirmation';

function fmtVnd(n: number) { return new Intl.NumberFormat('vi-VN').format(n) + 'đ'; }
/**
 * Date only — the order summary reads as a period, not a timestamp.
 *
 * `periodStart`/`periodEnd` are true UTC instants, NOT the house-frame wall
 * clock, so they format through an explicit Asia/Ho_Chi_Minh — the same way
 * SubscriptionTab's and InvoicesTab's fmtDate do (INVARIANTS §2). A bare
 * toLocaleDateString prints the viewer's own day, which can disagree with the
 * period on the invoice they are about to pay.
 */
function fmtDay(iso: string) {
  return new Date(iso).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
}

export function SubscriptionInvoicePage() {
  const { t } = useTranslation('billing');
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useSubscriptionInvoice(id);
  // The cancel dialog's term-warning copy must gate on whether the *subscription*
  // is currently on a trial or paid term — the same predicate SubscriptionTab's
  // pendingInvoiceHasFreeFallback uses — not on the invoice's extendsTerm alone.
  // A FREE-lapsed teacher (planId FREE, currentPeriodEnd already past — the
  // shape runSubscriptionTick leaves) buying PRO gets extendsTerm: true on this
  // invoice, but nothing live is at risk, so they must see the NEUTRAL copy too.
  const { data: sub } = useSubscription();
  const onTrial = sub?.status === 'TRIALING';
  const onPaidTerm = !!sub && !onTrial && sub.plan.code !== 'FREE';

  // Track what the page first saw, so a flip to PAID can be distinguished from
  // opening an invoice that was already settled before this visit.
  const firstStatusRef = useRef<string | null>(null);
  if (data && firstStatusRef.current === null) firstStatusRef.current = data.status;
  const justPaid =
    firstStatusRef.current !== null && isAwaitingPayment(firstStatusRef.current) && data?.status === 'PAID';

  // Nothing re-renders on its own once the 10-minute polling window closes —
  // the last poll is what stops. `startedAt`/`now` are lazily-initialized
  // state rather than `useRef(Date.now())` (kept pure under the
  // react-compiler purity rule); `now` only advances via the timeout below.
  // The timeout is scheduled to fire just *past* the deadline (`+ 1`), not
  // exactly at it: `shouldKeepPolling` uses an inclusive `<=`, so a timeout
  // firing exactly on the boundary would still read as "keep polling" and
  // `now` would never advance again after that — pinning `stillPolling` (and
  // the recheck affordance it gates) live forever on a screen that has
  // actually stopped polling.
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!data || !isAwaitingPayment(data.status)) return;
    const remaining = POLL_WINDOW_MS - (Date.now() - startedAt);
    const timeout = setTimeout(() => setNow(Date.now()), Math.max(remaining, 0) + 1);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the primitive status, not the whole `data` object (a new reference each poll)
  }, [data?.status, startedAt]);
  const stillPolling = !!data && shouldKeepPolling({ status: data.status, startedAt, now });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-4xl space-y-5">
          <Skeleton className="h-4 w-24" />
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start">
            <section className="rounded-lg border border-surface-200 bg-surface-0 p-6 space-y-4">
              <Skeleton className="h-3 w-32" />
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              <Skeleton className="h-8 w-40" />
            </section>
            <section className="rounded-lg border border-surface-200 bg-surface-0 p-6 space-y-4">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="mx-auto h-64 w-64" />
            </section>
          </div>
        </div>
      </div>
    );
  }
  // Gated on the absence of data, not on `error` alone: TanStack v5 keeps the
  // last-good `data` around and only sets `error` when a background refetch
  // fails. With `retry: 1` (see App.tsx), two failed poll ticks — a blip, not
  // an outage — would otherwise blank out a perfectly good QR/account
  // number/memo mid-transfer, only for it to reappear on the next tick.
  if (!data) {
    return (
      <div className="p-6 max-w-md mx-auto text-red-700 bg-red-50 border border-red-200 rounded-md">
        {(error as Error | null)?.message ?? 'Không tìm thấy hoá đơn này.'}
      </div>
    );
  }

  if (data.status === 'EXPIRED') {
    return (
      <div className="p-6 max-w-md mx-auto">
        <p className="text-surface-700 mb-3">{t('invoice.expired')}</p>
        <Link to="/settings/billing" className="text-brand-600 hover:underline">← {t('title')}</Link>
      </div>
    );
  }

  // Both the PENDING and PAID states share the same two-column shell: the order
  // summary is the payer's receipt of what they are buying and stays put, while
  // only the right column swaps between "how to pay" and "we got it". Replacing
  // the whole screen with a small confirmation card threw away the context and
  // read as a different page.
  const isPaid = data.status === 'PAID';

  // Null unless the server froze one at creation — see the schema docblock on
  // SubscriptionInvoice.prorationBreakdown for why it cannot be recomputed here.
  const breakdown = data.prorationBreakdown;

  // Composed exactly like SubscriptionTab's pendingInvoiceHasFreeFallback: the
  // server-side extendsTerm fact only means something is at risk when the
  // subscription itself is currently on a trial or a paid term.
  const invoiceThreatensTerm = data.extendsTerm && (onTrial || onPaidTerm);

  return (
    <div className="p-6">
      <div className="mx-auto max-w-4xl space-y-5">
        <Link to="/settings/billing" className="text-brand-600 hover:underline text-[13px]">
          ← {t('title')}
        </Link>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start">
          {/* ── Left: what is being bought ── */}
          <section className="rounded-lg border border-surface-200 bg-surface-0 p-6">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-400">
              {t('invoice.orderTitle')}
            </h2>

            <dl className="mt-4 space-y-3 text-[13px]">
              <div className="flex justify-between gap-4">
                <dt className="text-surface-500">{t('invoice.planLabel')}</dt>
                <dd className="font-semibold text-surface-900">{data.plan?.name ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-surface-500">{t('invoice.cycleLabel')}</dt>
                <dd className="text-surface-900">
                  {t(data.billingCycle === 'ANNUAL' ? 'invoice.cycleAnnual' : 'invoice.cycleMonthly')}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-surface-500">{t('invoice.periodLabel')}</dt>
                <dd className="text-right text-surface-900">
                  {fmtDay(data.periodStart)} – {fmtDay(data.periodEnd)}
                </dd>
              </div>
            </dl>

            {/* Where this number came from. Rendered only when the server froze
                a breakdown onto the invoice at creation — i.e. only for a
                prorated mid-cycle upgrade. Renewals, trial purchases and
                FREE → paid carry null and show nothing, because for them the
                total IS the whole story.

                Every money figure here is either a plan-price fact or the
                charged total; nothing is re-derived in the browser. The
                cross-cycle credit is the EFFECTIVE one (full price − charged),
                so the two lines balance to the đồng. */}
            {breakdown && (
              <div className="mt-5 border-t border-surface-200 pt-5">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-400">
                  {t('invoice.breakdown.title')}
                </h3>
                {breakdown.kind === 'SAME_CYCLE' ? (
                  <div className="mt-3 space-y-[6px] text-[13px] leading-[1.6] text-surface-600">
                    <p>
                      {t('invoice.breakdown.sameCycleLine', {
                        fromPlan: breakdown.fromPlanName,
                        toPlan: breakdown.toPlanName,
                        days: breakdown.remainingDays,
                      })}
                    </p>
                    <p>
                      {t('invoice.breakdown.sameCycleRate', {
                        diff: fmtVnd(breakdown.diffVnd),
                        cycleDays: breakdown.cycleDays,
                      })}
                    </p>
                    <p>
                      {t('invoice.breakdown.sameCycleKeepsRenewal', { date: fmtDay(data.periodEnd) })}
                    </p>
                  </div>
                ) : (
                  <>
                    <dl className="mt-3 space-y-[8px] text-[13px]">
                      <div className="flex justify-between gap-4">
                        <dt className="text-surface-500">
                          {t('invoice.breakdown.crossFullPrice', { toPlan: breakdown.toPlanName })}
                        </dt>
                        <dd className="tabular text-surface-900">{fmtVnd(breakdown.fullPriceVnd)}</dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-surface-500">
                          {t('invoice.breakdown.crossCredit', { fromPlan: breakdown.fromPlanName })}
                        </dt>
                        <dd className="tabular text-brand-700">−{fmtVnd(breakdown.creditVnd)}</dd>
                      </div>
                    </dl>
                    <p className="mt-[10px] text-[12.5px] leading-[1.6] text-surface-500">
                      {t('invoice.breakdown.crossNote', { fromPlan: breakdown.fromPlanName })}
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Frozen at invoice creation, exactly like the proration block above —
                see PromotionSnapshot's schema docblock for why a promotion's price
                cannot be recomputed here. A campaign can end, get edited, or be
                revoked after this invoice was issued; what the teacher was quoted
                and now owes must never move with it. No applyPercentOff call on
                this page — every figure below is read straight off
                promotionSnapshot, and listAmountVnd − discountVnd always equals
                data.amountVnd because the server derived them together. */}
            {data.discountVnd > 0 && data.promotionSnapshot && (
              <div className="mt-5 border-t border-surface-200 pt-5">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-400">
                  {t('invoice.discount.title')}
                </h3>
                <dl className="mt-3 space-y-[8px] text-[13px]">
                  <div className="flex justify-between gap-4">
                    <dt className="text-surface-500">{t('invoice.discount.listLabel')}</dt>
                    <dd className="tabular text-surface-900">
                      {fmtVnd(data.promotionSnapshot.listAmountVnd)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-surface-500">
                      {t('invoice.discount.line', {
                        name: data.promotionSnapshot.name,
                        pct: data.promotionSnapshot.percentOff,
                      })}
                    </dt>
                    <dd className="tabular text-brand-700">
                      −{fmtVnd(data.promotionSnapshot.discountVnd)}
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            <div className="mt-5 border-t border-surface-200 pt-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-400">
                {t('invoice.totalLabel')}
              </p>
              <p className="mt-1 text-[32px] font-bold leading-none text-surface-900">
                {fmtVnd(data.amountVnd)}
              </p>
            </div>
          </section>

          {/* ── Right: how to pay, or the confirmation once paid ── */}
          {isPaid ? (
            <PaymentConfirmation
              paidAt={data.paidAt ?? null}
              periodEnd={data.periodEnd ?? null}
              justPaid={justPaid}
            />
          ) : (
            <section className="rounded-lg border border-surface-200 bg-surface-0 p-6">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-400">
                {t('invoice.payTitle')}
              </h2>

              {data.qrUrl && (
                <div className="mx-auto mt-4 w-64">
                  <img src={data.qrUrl} alt="VietQR" className="w-full rounded-sm border border-surface-200" />
                  <p className="mt-2 text-center text-xs text-surface-400">{t('invoice.scanQr')}</p>
                </div>
              )}

              {/* Copy buttons only on the account number and the memo — the two
                  values a person retypes by hand and gets wrong. */}
              {data.bankInfo && (
                <dl className="mt-5 space-y-3 border-t border-surface-200 pt-5 text-[13px]">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <dt className="text-surface-500">{t('invoice.accountNo')}</dt>
                      <dd className="truncate font-mono font-semibold text-surface-900">
                        {data.bankInfo.accountNo}
                      </dd>
                    </div>
                    <CopyButton text={data.bankInfo.accountNo} />
                  </div>
                  <div>
                    <dt className="text-surface-500">{t('invoice.accountName')}</dt>
                    <dd className="font-medium text-surface-900">{data.bankInfo.accountName}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <dt className="text-surface-500">{t('invoice.ref')}</dt>
                      <dd className="truncate font-mono font-semibold text-surface-900">{data.paymentRef}</dd>
                    </div>
                    <CopyButton text={data.paymentRef} />
                  </div>
                </dl>
              )}
            </section>
          )}
        </div>

        {/* Back to promising automatic activation, because it is now true: the
            platform banks with MBBank, SePay signs the subscription IPN and
            POST /api/webhooks/sepay/subscription grants the plan. This block
            spent the Techcombank period saying the opposite — framed as
            temporary maintenance rather than a missing feature — and the
            comment then standing here asked for exactly this edit once the
            webhook was live.

            Zalo stays, demoted from the activation path to what it should be:
            where to go if something looks wrong. */}
        {!isPaid && (
          <div className="mx-auto max-w-xl rounded-lg border border-surface-200 bg-surface-50 px-4 py-3 text-center text-[12px] leading-relaxed text-surface-600">
            <p>{t('invoice.iPaidHint')}</p>
            <a
              href={ZALO_OA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block font-medium text-brand-700 underline hover:text-brand-800"
            >
              {t('invoice.iPaidZaloCta')} →
            </a>
          </div>
        )}

      {/* `invoice.waiting` / `invoice.stoppedWatching` (both locales) are
          deliberately unused here, not just untranslated: the only two things
          that ever set a SubscriptionInvoice to PAID are the SePay IPN
          reconciler and the dev-only mock-pay route below — the admin
          `POST /admin/orgs/:id/plan` activation path writes `Subscription`
          and `Organization.planId` but never touches this invoice. During the
          current Techcombank period SePay delivers no webhooks at all, so
          "Đang chờ chuyển khoản…" / "Đã ngừng tự kiểm tra sau 10 phút" would
          claim an automatic check that cannot possibly resolve this invoice —
          the same false promise this epic already removed from `iPaidHint`
          two lines up. The poll itself (`useSubscriptionInvoice`'s
          `refetchInterval`) keeps running regardless; only the claim about it
          is gone. Restore these keys once MBBank is linked and the SePay
          webhook points at `POST /api/webhooks/sepay/ipn` — at that point
          this screen genuinely is being watched on the teacher's behalf. */}
        {!isPaid && !stillPolling && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => refetch()}
              className="text-[13px] text-brand-600 underline hover:text-brand-700"
            >
              {t('invoice.recheck')}
            </button>
          </div>
        )}

        {/* Landing here is one click from the upgrade button, so this is where a
            teacher most often realises they picked the wrong plan or cycle.
            Meaningless once paid. */}
        {!isPaid && id && (
          <CancelInvoiceButton
            invoiceId={id}
            extendsTerm={invoiceThreatensTerm}
            // The plan IN USE, not the invoice's target plan: a Pro-trial org
            // buying PLUS must not be told "bạn vẫn dùng Plus" when they are
            // using Pro. Same source SubscriptionTab reads (`plan.name`).
            planName={sub?.plan.name ?? ''}
            // For a term-extending invoice the date the teacher cares about is
            // when the CURRENT term runs out — which is exactly this invoice's
            // periodStart (the tick stacks a renewal onto currentPeriodEnd,
            // subscriptionBilling.ts:409, and a trial purchase starts at the
            // trial's end). `periodEnd` is the end of the period being SOLD,
            // which is the wrong day to warn about. This date is only ever
            // interpolated into the TERM string above, so it stays keyed off
            // the invoice's raw extendsTerm regardless of invoiceThreatensTerm.
            periodEnd={data.extendsTerm ? data.periodStart : data.periodEnd}
          />
        )}

        {(import.meta.env.DEV || import.meta.env['VITE_ENABLE_MOCK_PAY'] === '1') && !isPaid && id && (
          <div className="mx-auto max-w-md">
            <DevMockPayButton invoiceId={id} />
          </div>
        )}
      </div>
    </div>
  );
}

function CancelInvoiceButton({
  invoiceId,
  extendsTerm,
  planName,
  periodEnd,
}: {
  invoiceId: string;
  extendsTerm: boolean;
  planName: string;
  /** ISO instant. See the call site: which date this is depends on extendsTerm. */
  periodEnd: string;
}) {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const cancelInvoice = useCancelSubscriptionInvoice();
  const [confirming, setConfirming] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className="text-center">
      <button
        type="button"
        disabled={cancelInvoice.isPending}
        onClick={() => setConfirming(true)}
        className="min-h-[44px] text-[13px] text-surface-500 underline hover:text-surface-700 disabled:opacity-50"
      >
        {t('pendingCallout.cancel')}
      </button>
      {failed && <p className="mt-1 text-xs text-red-600">{t('pendingCallout.cancelError')}</p>}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pendingCallout.cancelConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {/* Same distinction the billing tab's dialog makes
                  (pendingInvoiceHasFreeFallback): `extendsTerm` here is the
                  caller's already-composed `data.extendsTerm && (onTrial ||
                  onPaidTerm)`, not the invoice's raw server field alone — a
                  FREE-lapsed teacher buying up must see the neutral string
                  even though the invoice they're cancelling does extend a
                  term. `plan`/`date` are ignored by the neutral string, so one
                  call serves both. */}
              {t(extendsTerm ? 'pendingCallout.cancelConfirmTerm' : 'pendingCallout.cancelConfirm', {
                plan: planName,
                date: fmtDay(periodEnd),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('pendingCallout.cancelKeep')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setFailed(false);
                setConfirming(false);
                cancelInvoice.mutate(invoiceId, {
                  onSuccess: () => navigate('/settings/billing'),
                  onError: () => setFailed(true),
                });
              }}
            >
              {t('pendingCallout.cancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Dev/demo control mirroring the public invoice page's "Sandbox Testing"
 * button: automatic reconciliation of subscription payments isn't built yet,
 * so this is how the trial→paid flow gets exercised without a real transfer.
 * Rendered when `import.meta.env.DEV` (local `vite dev`) OR a REAL build was
 * baked with `VITE_ENABLE_MOCK_PAY=1` (set on the dev/demo web services,
 * never on prod — see `apps/web/app/components/invoice/PaymentPanel.tsx` for
 * the same pattern). The API route's `devOnly` middleware (NODE_ENV=production
 * → 404) is the hard second layer that still holds even if this flag were
 * ever mis-set on a prod deploy.
 */
function DevMockPayButton({ invoiceId }: { invoiceId: string }) {
  const { t } = useTranslation('billing');
  const mockPay = useMockPaySubscriptionInvoice();
  const [failed, setFailed] = useState(false);

  const handleClick = () => {
    setFailed(false);
    mockPay.mutate(invoiceId, {
      // No onSuccess write: the invoice flips to PAID via the poll picking up
      // the mutation's own cache invalidation, which is what the render
      // actually reacts to. There was never a UI consumer for a "success"
      // flag here — only the error path renders anything.
      onError: () => setFailed(true),
    });
  };

  return (
    <div className="rounded-sm border border-orange-200 bg-orange-50 p-3">
      <p className="mb-2 text-xs font-medium text-orange-700">{t('invoice.devMockPayHint')}</p>
      <BusyButton
        type="button"
        onClick={handleClick}
        busy={mockPay.isPending}
        busyLabel={t('invoice.devMockPay')}
        className="w-full rounded-sm bg-orange-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-600 disabled:bg-orange-300"
      >
        {t('invoice.devMockPay')}
      </BusyButton>
      {failed && (
        <p className="mt-2 text-xs text-red-600">{t('invoice.devMockPayError')}</p>
      )}
    </div>
  );
}
